/**
 * Utility functions for API calls
 */

/**
 * Enhanced fetch wrapper with better error handling
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @returns {Promise} - The response data or throws an error
 */
export const apiFetch = async (url, options = {}) => {
  try {
    // Set default headers
    const defaultHeaders = {
      'Accept': 'application/json',
    };

    // Merge headers, but don't override Content-Type if it's FormData
    const headers = options.body instanceof FormData 
      ? { ...defaultHeaders, ...options.headers }
      : { 'Content-Type': 'application/json', ...defaultHeaders, ...options.headers };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Check if response is ok
    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      
      try {
        // Try to parse error response as JSON
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch (parseError) {
        // If JSON parsing fails, try to get text
        try {
          const errorText = await response.text();
          errorMessage = errorText || errorMessage;
        } catch (textError) {
          // If both fail, use the default message
          console.error('Error parsing error response:', textError);
        }
      }
      
      throw new Error(errorMessage);
    }

    // For S3 uploads (PUT requests via presigned URLs), we don't expect JSON response
    if (options.method === 'PUT' && (url.includes('amazonaws.com') || url.includes('/uploads/'))) {
      return response; // Return the response object itself for S3 uploads
    }

    // Try to parse response as JSON
    try {
      return await response.json();
    } catch (parseError) {
      // If JSON parsing fails, return the response text
      return await response.text();
    }
  } catch (error) {
    // Enrich "Failed to fetch" with debug info
    if (error.message === 'Failed to fetch') {
      const online = navigator.onLine ? 'online' : 'offline';
      const detail = `Network error (${online}) – request to ${url.replace(/https?:\/\/[^/]+/, '')} failed. Please check your internet connection and try again.`;
      throw new Error(detail);
    }
    throw error;
  }
};

/**
 * Helper function to build query string from object
 * @param {Object} params - Object containing query parameters
 * @returns {string} - Query string
 */
export const buildQueryString = (params) => {
  const queryParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      queryParams.append(key, value);
    }
  });
  
  return queryParams.toString();
};

/**
 * Helper function to get API base URL
 * @returns {string} - Base API URL
 */
export const getApiBaseUrl = () => {
  return import.meta.env.VITE_API_URL || '/api';
};

/**
 * Helper function to check if running in development mode
 * @returns {boolean} - True if in development mode
 */
export const isDevelopment = () => {
  return import.meta.env.DEV || window.location.hostname === 'localhost';
};